// Ficha de una tarea: se abre desde cualquier lista (Tareas, Hoy, fichas, menu) para leerla completa, editarla,
// reasignarla, cambiar su estado o borrarla.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import * as crm from '../api/crm';
import type { Tarea } from '../api/types';
import { K, useMiembros } from '../data/queries';
import { ago, shortDate, time } from '../lib/format';
import { ErrorBox, Pill, Skeleton } from './bits';
import { Dialog } from './Dialog';
import { SelectorFecha } from './SelectorFecha';
import { abrirDialogo } from './forms';
import { Texto } from './Texto';
import { toast } from './toasts';

export const ESTADO_TAREA: Record<Tarea['estado'], string> = { TODO: 'Pendiente', IN_PROGRESS: 'En curso', DONE: 'Hecha' };
const TONO: Record<Tarea['estado'], 'warn' | 'acc' | 'ok'> = { TODO: 'warn', IN_PROGRESS: 'acc', DONE: 'ok' };

// Fecha y hora locales para los campos del formulario (el valor guardado es ISO en UTC).
const partes = (iso: string | null) => {
  if (!iso) return { fecha: '', hora: '09:00' };
  const d = new Date(iso);
  const dos = (n: number) => String(n).padStart(2, '0');
  return { fecha: `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`, hora: `${dos(d.getHours())}:${dos(d.getMinutes())}` };
};

export function TareaFicha({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useQuery({ queryKey: ['tarea', id], queryFn: () => crm.tarea(id) });
  const miembros = useMiembros();
  const [editando, setEditando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [error, setError] = useState('');

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['tarea', id] });
    void qc.invalidateQueries({ queryKey: K.tareas });
    void qc.invalidateQueries({ queryKey: ['tareas-hechas'] });
    void qc.invalidateQueries({ queryKey: ['timeline'] });
  };
  const nombreDe = (mid: string | null) => (mid ? (miembros.data?.find((m) => m.id === mid)?.nombre ?? 'Otra persona') : 'Sin asignar');

  const cambiarEstado = async (estado: Tarea['estado']) => {
    try {
      await crm.cambiarEstadoTarea(id, estado);
      refrescar();
      toast(estado === 'DONE' ? 'Tarea hecha.' : estado === 'TODO' ? 'Tarea reabierta.' : 'Tarea en curso.');
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const borrar = async () => {
    if (!confirmar) return setConfirmar(true);
    try {
      await crm.borrarTarea(id);
      refrescar();
      toast('Tarea borrada.');
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (t.isPending)
    return (
      <Dialog titulo="Tarea" onClose={onClose} width={620}>
        <Skeleton rows={5} />
      </Dialog>
    );
  if (t.error)
    return (
      <Dialog titulo="Tarea" onClose={onClose} width={620}>
        <ErrorBox error={t.error} onRetry={() => t.refetch()} />
      </Dialog>
    );
  const x = t.data;
  if (editando) return <TareaEditar t={x} onClose={() => setEditando(false)} onGuardada={refrescar} />;

  const vencida = x.vence && x.estado !== 'DONE' && new Date(x.vence) < new Date();
  return (
    <Dialog titulo={x.titulo} subtitulo={x.creadoPor ? `Creada por ${x.creadoPor} · ${ago(x.creado)}` : `Creada ${ago(x.creado)}`} onClose={onClose} width={620}>
      <div className="form">
        <dl className="kv">
          <dt>Estado</dt>
          <dd>
            <Pill tone={TONO[x.estado]} dot>
              {ESTADO_TAREA[x.estado]}
            </Pill>
          </dd>
          <dt>Vence</dt>
          <dd className={vencida ? 'bad' : undefined}>{x.vence ? `${shortDate(x.vence)} · ${time(x.vence)}${vencida ? ' · vencida' : ''}` : 'Sin fecha'}</dd>
          <dt>Responsable</dt>
          <dd>{nombreDe(x.asignadoId)}</dd>
          {x.persona ? (
            <>
              <dt>Contacto</dt>
              <dd>
                <Link to={`/personas/${x.persona.id}`} onClick={() => abrirDialogo(null)}>
                  {x.persona.nombre}
                </Link>
              </dd>
            </>
          ) : null}
          {x.empresa ? (
            <>
              <dt>Empresa</dt>
              <dd>
                <Link to={`/empresas/${x.empresa.id}`} onClick={() => abrirDialogo(null)}>
                  {x.empresa.nombre}
                </Link>
              </dd>
            </>
          ) : null}
          {x.oportunidad ? (
            <>
              <dt>Oportunidad</dt>
              <dd>
                <button type="button" className="link" onClick={() => abrirDialogo({ tipo: 'verOportunidad', id: x.oportunidad!.id })}>
                  {x.oportunidad.nombre}
                </button>
              </dd>
            </>
          ) : null}
        </dl>
        <div className="tarea-cuerpo">{x.cuerpo.trim() ? <Texto texto={x.cuerpo} /> : <p className="sb">Sin descripción.</p>}</div>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          <button type="button" className={`btn ${confirmar ? 'danger' : 'ghost'}`} onClick={borrar} style={{ marginRight: 'auto' }}>
            {confirmar ? 'Sí, borrar tarea' : 'Borrar'}
          </button>
          <button type="button" className="btn" onClick={() => setEditando(true)}>
            Editar
          </button>
          {x.estado === 'DONE' ? (
            <button type="button" className="btn" onClick={() => cambiarEstado('TODO')}>
              Reabrir
            </button>
          ) : (
            <>
              {x.estado === 'TODO' ? (
                <button type="button" className="btn" onClick={() => cambiarEstado('IN_PROGRESS')}>
                  Empezar
                </button>
              ) : null}
              <button type="button" className="btn primary" onClick={() => cambiarEstado('DONE')}>
                Marcar hecha
              </button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function TareaEditar({ t, onClose, onGuardada }: { t: Tarea; onClose: () => void; onGuardada: () => void }) {
  const miembros = useMiembros();
  const inicio = partes(t.vence);
  const [v, setV] = useState({ titulo: t.titulo, cuerpo: t.cuerpo, fecha: inicio.fecha, hora: inicio.hora, estado: t.estado, asignadoId: t.asignadoId ?? '' });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!v.titulo.trim()) return setError('Escribe qué hay que hacer.');
    setGuardando(true);
    try {
      await crm.editarTarea(t.id, {
        titulo: v.titulo,
        cuerpo: v.cuerpo,
        vence: v.fecha ? new Date(`${v.fecha}T${v.hora || '09:00'}:00`).toISOString() : null,
        estado: v.estado,
        asignadoId: v.asignadoId || null,
      });
      onGuardada();
      toast('Tarea guardada.');
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo="Editar tarea" onClose={onClose} width={620}>
      <form className="form" onSubmit={submit}>
        <label>
          Qué hay que hacer
          <input id="te-titulo" value={v.titulo} onChange={(e) => setV({ ...v, titulo: e.target.value })} required />
        </label>
        <label>
          Descripción
          <textarea id="te-cuerpo" value={v.cuerpo} onChange={(e) => setV({ ...v, cuerpo: e.target.value })} rows={7} placeholder="Detalles, pasos (una línea que empiece con - o 1. se vuelve lista)…" />
        </label>
        <div className="grid2">
          <label>
            Día
            <SelectorFecha id="te-fecha" value={v.fecha} onChange={(x) => setV({ ...v, fecha: x })} etiqueta="Fecha límite" />
          </label>
          <label>
            Hora
            <input id="te-hora" type="time" value={v.hora} onChange={(e) => setV({ ...v, hora: e.target.value })} />
          </label>
        </div>
        <div className="grid2">
          <label>
            Responsable
            <select id="te-responsable" value={v.asignadoId} onChange={(e) => setV({ ...v, asignadoId: e.target.value })}>
              <option value="">Sin asignar</option>
              {(miembros.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </label>
          <label>
            Estado
            <select id="te-estado" value={v.estado} onChange={(e) => setV({ ...v, estado: e.target.value as Tarea['estado'] })}>
              {(Object.keys(ESTADO_TAREA) as Tarea['estado'][]).map((k) => (
                <option key={k} value={k}>
                  {ESTADO_TAREA[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose} data-close>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
